package horcrux.frontend.common.views.collections.importdocuments

import arrow.core.Either
import arrow.core.NonEmptyList
import arrow.core.None
import arrow.core.left
import arrow.core.raise.either
import arrow.core.some
import horcrux.common.HorcruxEither
import horcrux.common.api.APILimits
import horcrux.common.api.dtos.LocalDocument
import horcrux.common.api.dtos.LocalRawDocumentIdentifier
import horcrux.common.api.dtos.PostRawDocumentsErrorReason
import horcrux.common.api.dtos.PostRawDocumentsResponse
import horcrux.common.api.dtos.RawDocumentClientSideID
import horcrux.common.api.dtos.RemoteRawDocumentIdentifier
import horcrux.common.api.dtos.types.CollectionMetadata
import horcrux.common.api.dtos.types.DocumentIdentifier
import horcrux.common.api.dtos.types.DocumentURL
import horcrux.common.api.dtos.types.RawDocumentName
import horcrux.common.extensions.head
import horcrux.common.extensions.replaceLast
import horcrux.common.types.NonEmptyMap
import horcrux.common.types.NonEmptyMap.Companion.nonEmptyMapOf
import horcrux.common.utils.eitherWay
import horcrux.common.utils.raiseError
import horcrux.common.utils.safelet.safeLet
import horcrux.common.utils.toOptional
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.DocumentInputData
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.LocalDocumentInput
import horcrux.frontend.common.ViewRefreshState
import horcrux.frontend.common.clients.backendclient.AuthenticatedBackendClient
import horcrux.frontend.common.handleResponseException
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpStatusCode.Companion.BadGateway
import io.ktor.http.HttpStatusCode.Companion.Conflict
import io.ktor.http.HttpStatusCode.Companion.NotFound
import io.ktor.http.HttpStatusCode.Companion.TooManyRequests
import io.ktor.http.HttpStatusCode.Companion.UnsupportedMediaType
import io.ktor.util.encodeBase64
import mui.material.AlertColor

suspend fun AuthenticatedBackendClient.importDocumentAndHandleResponse(
    documentIdentifiers: NonEmptyList<DocumentIdentifier>,
    collectionMetadata: CollectionMetadata,
    globalAppState: GlobalAppState,
    viewRefreshState: ViewRefreshState,
    onSuccess: () -> Unit = {}
): HorcruxEither<Unit> {
    val updateBanner = globalAppState.updateBanner
    updateBanner(AlertColor.info, "Importing documents")
    return this.importDocuments(
        collectionMetadata.id,
        documentIdentifiers,
        globalAppState.embeddingModelConfigsState.embeddingModelConfigs
    )
        .handleResponseException { cause ->
            when (cause.response.status) {
                NotFound -> {
                    updateBanner(
                        AlertColor.error,
                        "Sorry we could not find a document at this URL."
                    )
                    None
                }

                UnsupportedMediaType -> {
                    updateBanner(
                        AlertColor.error,
                        "Sorry this document type is not currently supported. Feel free to make a request to support it."
                    )
                    None
                }

                TooManyRequests -> {
                    updateBanner(
                        AlertColor.error,
                        "Sorry the document URL is getting too many requests right now. Please try again later or try downloading to your computer and uploading it as a file."
                    )
                    None
                }

                Conflict -> {
                    updateBanner(
                        AlertColor.error,
                        "Document names in a collection must be unique."
                    )
                    None
                }

                BadGateway -> {
                    updateBanner(
                        AlertColor.error,
                        "There was an error indexing your documents. This is likely due to a problem outside of our control. Please try again later."
                    )
                    None
                }

                else -> cause.some()
            }
        }
        .map {
            onSuccess()
            updateBanner(AlertColor.success, "Documents successfully imported!")
            viewRefreshState.toggle()
        }
}

suspend fun GlobalAppState.uploadRawDocument(
    documentInputData: DocumentInputData,
    isLoadingState: BooleanState,
    block: (NonEmptyMap<RawDocumentClientSideID, DocumentInputData>) -> NonEmptyMap<RawDocumentClientSideID, DocumentInputData> = { it },
): Either<Throwable, PostRawDocumentsResponse> {
    val rawDocumentName = RawDocumentName(documentInputData.documentName.value)
    return safeLet(documentInputData.documentInputMaybe.valueMaybe) { documentInput ->
        isLoadingState.setTrue()
        this@uploadRawDocument.updateBanner(
            AlertColor.info, "Uploading ${rawDocumentName.betterToString()}"
        )
        this@uploadRawDocument.submitBackendAPIRequest { backendClient ->
            val rawDocumentIdentifier = when (documentInput) {
                is DocumentURL -> RemoteRawDocumentIdentifier.invoke(
                    documentInputData.rawDocumentClientSideID,
                    documentInput,
                    rawDocumentName
                )

                is LocalDocumentInput -> LocalRawDocumentIdentifier(
                    documentInputData.rawDocumentClientSideID,
                    LocalDocument(documentInput.toByteArray()),
                    rawDocumentName
                )

                else -> raiseError("DocumentInput $documentInput is unsupported")
            }
            backendClient
                .postRawDocuments(nonEmptyMapOf(documentInputData.rawDocumentClientSideID to rawDocumentIdentifier))
                .onLeft { cause ->
                    (cause as? ClientRequestException)?.let { cause ->
                        PostRawDocumentsErrorReason.deserializeFromBase64String(
                            cause.response.bodyAsText().encodeBase64()
                        )
                            .bind()
                            .errorTypeByClientSideID
                            .forEach { entry ->
                                val rawDocumentClientSideID = entry.key
                                val postRawDocumentsErrorType = entry.value
                                this@uploadRawDocument.documentInputDataByClientSideIDState.update { documentInputDataByClientSideID ->
                                    documentInputDataByClientSideID
                                        .replace(
                                            rawDocumentClientSideID,
                                            documentInputData.copy(postRawDocumentsErrorTypeMaybe = postRawDocumentsErrorType)
                                        )
                                }
                            }
                    }
                    this@uploadRawDocument.updateBanner(
                        AlertColor.error, "There was an error uploading your document"
                    )
                }.onRight { postRawDocumentsResponse ->
                    this@uploadRawDocument.updateBanner(
                        AlertColor.success, "Successfully uploaded your document!"
                    )
                    this@uploadRawDocument.documentInputDataByClientSideIDState.update { documentInputDataByClientSideID ->
                        documentInputDataByClientSideID
                            .replaceLast(documentInputData.copy(rawDocumentMetadataMaybe = postRawDocumentsResponse.rawDocumentMetadataClientSideIDList.head.second))
                            .let(block)
                    }
                }
                .eitherWay { isLoadingState.setFalse() }
                .bind()

        }
    } ?: run {
        this@uploadRawDocument.updateBanner(
            AlertColor.error, "Please input a document"
        )
        IllegalArgumentException("Document input for ${documentInputData.rawDocumentClientSideID} is empty").left()
    }
}

suspend fun GlobalAppState.uploadRawDocumentAndAddNewDocumentInput(
    documentInputData: DocumentInputData,
    isLoadingState: BooleanState,
): Either<Throwable, Unit> = either {
    val documentInputDataByClientSideID =
        this@uploadRawDocumentAndAddNewDocumentInput.documentInputDataByClientSideIDState.get()
    safeLet(
        documentInputData.rawDocumentMetadataMaybe,
        (documentInputDataByClientSideID.size >= APILimits.MAX_POST_DOCUMENTS_BATCH_SIZE).toOptional()
    ) { _, _ ->
        val message = "You cannot upload more than ${APILimits.MAX_POST_DOCUMENTS_BATCH_SIZE} at once"
        this@uploadRawDocumentAndAddNewDocumentInput.updateBanner(AlertColor.error, message)
    } ?: run {
        this@uploadRawDocumentAndAddNewDocumentInput.uploadRawDocument(documentInputData, isLoadingState) {
            if (documentInputDataByClientSideID.size < APILimits.MAX_POST_DOCUMENTS_BATCH_SIZE) {
                val rawDocumentClientSideID =
                    RawDocumentClientSideID(documentInputDataByClientSideID.size)
                it.plus(
                    rawDocumentClientSideID to DocumentInputData(
                        rawDocumentClientSideID,
                        documentInputDataByClientSideID.head.second.documentInputMaybe.documentInputType.getDocumentInputMaybe()
                    )
                )
            } else {
                it
            }
        }.bind()
    }
}
